import * as React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { ISelectableMetric, ITest } from '../../../../data/types';
import { roundValue } from '../../../../utils/math';

interface ITopScoreEntryProps {
  test: ITest;
  metric: ISelectableMetric;
  onTestClick: (test: ITest) => void;
}

const TopScoreEntry = ({
  test,
  metric,
  onTestClick,
}: ITopScoreEntryProps): React.ReactElement => (
  <Flex
    flex={1}
    align='center'
    px={4}
    cursor='pointer'
    onClick={(): void => onTestClick(test)}
    transition='all 0.2s ease-in-out'
    _hover={{ opacity: 0.9 }}
    role='button'
    aria-label={`View test details for ${test.athlete?.name}`}
    tabIndex={0}
    onKeyDown={(e: React.KeyboardEvent): void => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onTestClick(test);
      }
    }}
  >
    <Text
      fontSize='48px'
      fontWeight={800}
      color='var(--scoreboard-secondary, #4285f4)'
      minW='80px'
    >
      1st
    </Text>
    <Text
      flex={1}
      fontSize='32px'
      fontWeight={700}
      color='#ffffff'
      textTransform='uppercase'
      letterSpacing='1px'
      textAlign='center'
    >
      {test.athlete?.name}
    </Text>
    {metric && (
      <Flex align='baseline' gap={2}>
        <Text fontSize='40px' fontWeight={700} color='#ffffff'>
          {roundValue(
            test[metric.value]?.value,
            test[metric.value]?.roundingPrecision
          )}
        </Text>
        <Text fontSize='20px' color='rgba(255, 255, 255, 0.8)'>
          {test[metric.value]?.units}
        </Text>
      </Flex>
    )}
  </Flex>
);

export interface ITopScoresBannerProps {
  leftTest?: ITest;
  rightTest?: ITest;
  metric: ISelectableMetric;
  onTestClick: (test: ITest) => void;
  isSplit?: boolean;
}

export const TopScoresBanner = ({
  leftTest,
  rightTest,
  metric,
  onTestClick,
  isSplit = false,
}: ITopScoresBannerProps): React.ReactElement | null => {
  if (!leftTest && !rightTest) return null;

  return (
    <Flex
      w='auto'
      mx='-40px'
      bg='var(--scoreboard-banner-bg, #dc143c)'
      px='40px'
      py={6}
      mb={4}
      gap={4}
      role='region'
      aria-label='Top scores'
      css={{
        '@media (min-width: 1200px)': {
          marginLeft: '-64px',
          marginRight: '-64px',
          paddingLeft: '64px',
          paddingRight: '64px',
        },
      }}
    >
      {leftTest && (
        <TopScoreEntry
          test={leftTest}
          metric={metric}
          onTestClick={onTestClick}
        />
      )}

      {isSplit && leftTest && rightTest && (
        <Box w='2px' bg='rgba(255, 255, 255, 0.3)' alignSelf='stretch' />
      )}

      {isSplit && rightTest && (
        <TopScoreEntry
          test={rightTest}
          metric={metric}
          onTestClick={onTestClick}
        />
      )}
    </Flex>
  );
};
